import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/shared/ui/dialog";
import { Button } from "@/shared/ui/button";
import { Input } from "@/shared/ui/input";
import { Label } from "@/shared/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/shared/ui/select";
import { Plus, X } from "lucide-react";
import { supabase } from "@/shared/integrations/supabase/client";
import { useToast } from "@/shared/hooks/use-toast";
import { useAssetCategories } from "@/features/site-survey-editor/pages/hooks/useAssetCategories";

interface ManageCategoryDescriptionsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ManageCategoryDescriptionsModal({ open, onOpenChange }: ManageCategoryDescriptionsModalProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { data: categories = [], isLoading } = useAssetCategories();

  const [selectedId, setSelectedId] = useState<string>("");
  const [descriptions, setDescriptions] = useState<string[]>([]);
  const [newValue, setNewValue] = useState("");
  const [saving, setSaving] = useState(false);

  // Load the selected category's descriptions into the working copy.
  useEffect(() => {
    const category = categories.find((c) => c.id === selectedId);
    setDescriptions(category ? [...category.descriptions] : []);
    setNewValue("");
  }, [selectedId, categories]);

  // Reset on close so a reopen starts clean.
  useEffect(() => {
    if (!open) {
      setSelectedId("");
      setDescriptions([]);
      setNewValue("");
    }
  }, [open]);

  const handleAdd = () => {
    const trimmed = newValue.trim();
    if (!trimmed) return;
    if (descriptions.includes(trimmed)) {
      toast({ title: "Already added", description: `"${trimmed}" is already in the list.` });
      return;
    }
    setDescriptions((prev) => [...prev, trimmed]);
    setNewValue("");
  };

  const handleRemove = (value: string) => {
    setDescriptions((prev) => prev.filter((d) => d !== value));
  };

  const handleSave = async () => {
    if (!selectedId) return;
    setSaving(true);
    try {
      const { error } = await supabase
        .from("asset_categories")
        .update({ descriptions })
        .eq("id", selectedId);

      if (error) throw error;

      await queryClient.invalidateQueries({ queryKey: ["asset-categories"] });
      toast({ title: "Saved", description: "Descriptions updated." });
      onOpenChange(false);
    } catch (err) {
      toast({
        title: "Save failed",
        description: (err as Error).message,
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Manage Asset Categories Subtypes</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="category-select">Category</Label>
            <Select value={selectedId} onValueChange={setSelectedId} disabled={isLoading}>
              <SelectTrigger id="category-select">
                <SelectValue placeholder={isLoading ? "Loading..." : "Select a category"} />
              </SelectTrigger>
              <SelectContent>
                {categories.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {selectedId && (
            <>
              <div className="space-y-2">
                <Label>Subtypes</Label>
                {descriptions.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No subtypes yet.</p>
                ) : (
                  <div className="max-h-56 overflow-y-auto space-y-1.5 pr-1">
                    {descriptions.map((d) => (
                      <div
                        key={d}
                        className="flex items-center justify-between gap-2 rounded-md border px-3 py-1.5 text-sm"
                      >
                        <span className="truncate">{d}</span>
                        <button
                          type="button"
                          onClick={() => handleRemove(d)}
                          className="text-muted-foreground hover:text-destructive transition-colors shrink-0"
                          aria-label={`Remove ${d}`}
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex items-center gap-2">
                <Input
                  value={newValue}
                  onChange={(e) => setNewValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleAdd();
                    }
                  }}
                  placeholder="Add a subtype..."
                />
                <Button type="button" variant="outline" onClick={handleAdd} disabled={!newValue.trim()}>
                  <Plus className="h-4 w-4 mr-1" />
                  Add
                </Button>
              </div>
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={!selectedId || saving}>
            {saving ? "Saving..." : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
